import { useState, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "../../../assets/icons/export.png";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import {
  getFormattedDate,
  scrollToTop,
  getUserEmail
} from "../../../utils/commonFunctions";
import UserCreditsDetailsPage from "./UserCreditsDetailsPage";
import AddCreditsDialog from "./components/AddCreditsDialog";
import CustomButton from "../../../components/CustomButton/Button";

interface UserCreditsTabProps {
  userId: string;
  refreshSignal?: number;
}

const apiEnpoint: any = process.env.REACT_APP_API_BASE_URL;

const UserCreditsTab = ({ userId, refreshSignal }: UserCreditsTabProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [openAddCreditsDialog, setOpenAddCreditsDialog] = useState(false);
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>(
    []
  );
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const downloadCreditHistory = async (row: any) => {
    setMessage(null);
    if (!userId || !row?.MerchantId) return;

    const rowId = `${row.MerchantId}`;
    setDownloadingRowId(rowId);

    try {
      const url = `${apiEnpoint}/loyaltyprogram/credits/user/${userId}/business/${row.MerchantId}/export`;

      await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      // Get user email for the success message
      const userEmail = await getUserEmail();

      setMessage({
        type: "success",
        text: `Credit history will be emailed to ${userEmail} shortly.`
      });
      scrollToTop();
    } catch (error: any) {
      console.error("Failed to export credit history:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to export credit history. Please try again."
      });
      scrollToTop();
    } finally {
      setDownloadingRowId(null);
    }
  };

  const fetchCredits = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${apiEnpoint}/loyaltyprogram/credits/user/${userId}`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => setCredits(res.data))
      .catch(() => setError("Failed to fetch credits"))
      .finally(() => setLoading(false));
  }, [userId]);

  const fetchBusinesses = useCallback(() => {
    axios
      .get(`${apiEnpoint}/business`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        setBusinesses(
          res.data.map((b: any) => ({
            id: b.Id || b.id,
            name: b.establishmentname || b.name
          }))
        );
      })
      .catch(() => setBusinesses([]));
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [userId, fetchCredits, refreshSignal]);

  useEffect(() => {
    if (openAddCreditsDialog) fetchBusinesses();
  }, [openAddCreditsDialog, fetchBusinesses]);

  const handleShowDetails = (merchant: any) => {
    setSelectedMerchant(merchant);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedMerchant(null);
  };

  // Define columns for the credits table
  const columns = [
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "TotalPoints",
      label: "Total Credits",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? `$${value}` : "-")
    },
    {
      id: "LastCreditCreated",
      label: "Last Credit Created",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "MerchantId",
      label: "Merchant ID",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "Export",
      label: "Export",
      minWidth: 100,
      align: "left",
      format: (_value: any, row: any) => {
        const rowId = `${row.MerchantId}`;
        const isDownloading = downloadingRowId === rowId;
        return (
          <Tooltip title="Export credit history">
            <IconButton
              size="small"
              disabled={isDownloading}
              onClick={(e) => {
                e.stopPropagation();
                downloadCreditHistory(row);
              }}
            >
              {isDownloading ? (
                <CircularProgress size={16} />
              ) : (
                <img
                  src={FileDownloadIcon}
                  alt="export file"
                  title="Export"
                  style={{ cursor: "pointer", width: "20px", height: "20px" }}
                />
              )}
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  return (
    <Box>
      {message && (
        <div style={{ marginBottom: "20px" }}>
          <Alert severity={message.type as any}>{message.text}</Alert>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%"
          }}
        >
          <CustomButton callBack={() => setOpenAddCreditsDialog(true)}>
            Add Credit
          </CustomButton>
          <RefreshIcon onClick={fetchCredits} />
        </div>
      </div>

      <CustomTableWithPagination
        columns={columns}
        data={credits?.data || []}
        apiStatus={loading ? "fetching" : "idle"}
        payloadSize={100}
        currentPage={1}
        totalCount={credits?.data?.length || 0}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No data found."
        handleRowClick={handleShowDetails}
        sx={{ cursor: "pointer" }}
      />

      {selectedMerchant && (
        <UserCreditsDetailsPage
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          userId={userId}
          merchantId={selectedMerchant.MerchantId}
          establishmentName={selectedMerchant.EstablishmentName}
        />
      )}
      <AddCreditsDialog
        open={openAddCreditsDialog}
        onClose={() => {
          setOpenAddCreditsDialog(false);
        }}
        userId={userId}
        businesses={businesses}
        onSuccess={() => {
          fetchCredits();
          setOpenAddCreditsDialog(false);
        }}
      />
    </Box>
  );
};

export default UserCreditsTab;
